import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Copy, Loader2, Monitor, Shield, ShieldOff, Trash2, X } from "lucide-react";
import investorPortalApi from "@/lib/investorPortalApi";
import { useInvestorPortalAuth } from "@/contexts/InvestorPortalAuthContext";

const formatDate = (iso, lang = "de") => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-US" : "de-DE", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function InvestorSecurity() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const { session, refreshSession } = useInvestorPortalAuth();
  const twofaEnabled = session?.me?.security?.twofa_enabled;
  const recoveryLeft = session?.me?.security?.recovery_codes_remaining || 0;

  // 2FA setup
  const [setupPhase, setSetupPhase] = useState("idle"); // idle | loading | qr | verify | codes
  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  // 2FA disable
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableLoading, setDisableLoading] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  useEffect(() => {
    investorPortalApi.get("/sessions")
      .then(({ data }) => setSessions(data.sessions || []))
      .finally(() => setSessionsLoading(false));
  }, []);

  const startSetup = async () => {
    setSetupPhase("loading");
    try {
      const { data } = await investorPortalApi.post("/2fa/setup");
      setSetupData(data);
      setSetupPhase("qr");
    } catch (_e) {
      toast.error(t("invPortal.security.setupFailed"));
      setSetupPhase("idle");
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    if (!setupCode.trim()) return;
    setSetupPhase("verify");
    try {
      const { data } = await investorPortalApi.post("/2fa/verify-setup", { code: setupCode.trim() });
      setRecoveryCodes(data.recovery_codes || []);
      setSetupPhase("codes");
      await refreshSession();
      toast.success(t("invPortal.security.twofaEnabled"));
    } catch (_e) {
      toast.error(t("invPortal.security.codeInvalid"));
      setSetupPhase("qr");
    }
  };

  const disable2fa = async (e) => {
    e.preventDefault();
    if (!disableCode.trim()) return;
    setDisableLoading(true);
    try {
      await investorPortalApi.post("/2fa/disable", { code: disableCode.trim() });
      await refreshSession();
      setDisableOpen(false);
      setDisableCode("");
      toast.success(t("invPortal.security.twofaDisabled"));
    } catch (_e) {
      toast.error(t("invPortal.security.codeInvalid"));
    } finally {
      setDisableLoading(false);
    }
  };

  const revokeSession = async (id) => {
    try {
      await investorPortalApi.delete(`/sessions/${id}`);
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success(t("invPortal.security.sessionRevoked"));
    } catch (_e) {
      toast.error(t("invPortal.security.revokeFailed"));
    }
  };

  const revokeAll = async () => {
    try {
      await investorPortalApi.post("/sessions/all");
      setSessions([]);
      toast.success(t("invPortal.security.allRevoked"));
    } catch (_e) {
      toast.error(t("invPortal.security.revokeFailed"));
    }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="investor-portal-security">
      <motion.header initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}>
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.security.eyebrow")}</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-cocoa leading-[1.05]">{t("invPortal.security.title")}</h1>
        <p className="mt-3 text-warmGrey">{t("invPortal.security.lead")}</p>
      </motion.header>

      {/* 2FA Section */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
        data-testid="inv-security-2fa"
      >
        <div className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-lavenderFog" aria-hidden />
        <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.security.twofaEyebrow")}</p>
        <h2 className="font-serif text-xl text-cocoa mb-4">{t("invPortal.security.twofaTitle")}</h2>

        {twofaEnabled ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-sageMint flex items-center justify-center text-cocoa"><Shield size={18} /></div>
              <div>
                <p className="text-sm text-cocoa font-medium">{t("invPortal.security.twofaActiveLabel")}</p>
                <p className="text-xs text-warmGrey">{t("invPortal.security.recoveryCodes", { n: recoveryLeft })}</p>
              </div>
            </div>
            {!disableOpen ? (
              <button type="button" onClick={() => setDisableOpen(true)}
                className="self-start text-sm text-coral hover:text-raspberry inline-flex items-center gap-2 px-4 py-2 rounded-full border border-roseMilk hover:bg-roseMilk transition-colors"
                data-testid="inv-2fa-disable-btn"
              >
                <ShieldOff size={14} /> {t("invPortal.security.twofaDisableBtn")}
              </button>
            ) : (
              <form onSubmit={disable2fa} className="flex flex-col gap-3 mt-2 p-4 rounded-2xl bg-roseMilk/30 border border-roseMilk" data-testid="inv-2fa-disable-form">
                <p className="text-sm text-cocoa">{t("invPortal.security.disablePrompt")}</p>
                <input type="text" inputMode="numeric" maxLength={12} value={disableCode} onChange={(e) => setDisableCode(e.target.value)}
                  placeholder="123456" className="bg-white border border-sandBeige rounded-2xl px-4 py-3 text-center text-lg tracking-widest focus:border-raspberry focus:outline-none"
                  data-testid="inv-2fa-disable-input" />
                <div className="flex gap-2">
                  <button type="submit" disabled={disableLoading}
                    className="bg-coral hover:bg-raspberry disabled:opacity-60 text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
                    data-testid="inv-2fa-disable-submit"
                  >
                    {disableLoading ? <Loader2 size={14} className="animate-spin" /> : <ShieldOff size={14} />} {t("invPortal.security.twofaDisableConfirm")}
                  </button>
                  <button type="button" onClick={() => { setDisableOpen(false); setDisableCode(""); }}
                    className="text-sm text-warmGrey hover:text-cocoa px-4 py-2.5 rounded-full"
                  >
                    {t("invPortal.security.cancel")}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-roseMilk flex items-center justify-center text-coral"><ShieldOff size={18} /></div>
              <p className="text-sm text-cocoa">{t("invPortal.security.twofaInactiveLabel")}</p>
            </div>

            {setupPhase === "idle" && (
              <button type="button" onClick={startSetup}
                className="self-start bg-raspberry hover:bg-coral text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
                data-testid="inv-2fa-setup-btn"
              >
                <Shield size={14} /> {t("invPortal.security.twofaSetupBtn")}
              </button>
            )}

            {setupPhase === "loading" && <Loader2 className="animate-spin text-raspberry" size={24} />}

            {setupPhase === "qr" && setupData && (
              <div className="flex flex-col gap-4 mt-2" data-testid="inv-2fa-qr-step">
                <p className="text-sm text-cocoa">{t("invPortal.security.qrInstruction")}</p>
                <div className="mx-auto">
                  <img src={setupData.qr_code_data_url} alt="2FA QR Code" className="w-48 h-48 rounded-xl border border-sandBeige" data-testid="inv-2fa-qr-img" />
                </div>
                <div className="text-center">
                  <p className="text-xs text-warmGrey mb-1">{t("invPortal.security.manualKey")}</p>
                  <code className="text-sm text-cocoa bg-ivory px-3 py-1 rounded-lg select-all" data-testid="inv-2fa-secret">{setupData.secret}</code>
                </div>
                <form onSubmit={verifySetup} className="flex flex-col gap-3" data-testid="inv-2fa-verify-form">
                  <input type="text" inputMode="numeric" maxLength={6} value={setupCode} onChange={(e) => setSetupCode(e.target.value)}
                    placeholder="123456" className="bg-porcelain border border-sandBeige rounded-2xl px-4 py-3 text-center text-lg tracking-widest focus:border-raspberry focus:outline-none"
                    data-testid="inv-2fa-setup-code" />
                  <button type="submit" className="bg-raspberry hover:bg-coral text-white text-sm rounded-full px-5 py-2.5 inline-flex items-center justify-center gap-2 transition-colors"
                    data-testid="inv-2fa-verify-submit"
                  >
                    <Check size={14} /> {t("invPortal.security.twofaVerify")}
                  </button>
                </form>
              </div>
            )}

            {setupPhase === "verify" && <Loader2 className="animate-spin text-raspberry" size={24} />}

            {setupPhase === "codes" && recoveryCodes.length > 0 && (
              <div className="mt-2 p-5 rounded-2xl bg-sageMint/30 border border-sageMint" data-testid="inv-2fa-recovery-codes">
                <p className="text-sm text-cocoa font-medium mb-3">{t("invPortal.security.recoveryTitle")}</p>
                <p className="text-xs text-warmGrey mb-3">{t("invPortal.security.recoveryHint")}</p>
                <div className="grid grid-cols-2 gap-2">
                  {recoveryCodes.map((c) => (
                    <code key={c} className="text-sm text-cocoa bg-white px-3 py-2 rounded-lg text-center select-all">{c}</code>
                  ))}
                </div>
                <button type="button"
                  onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success(t("invPortal.security.copied")); }}
                  className="mt-4 text-sm text-raspberry hover:text-coral inline-flex items-center gap-2"
                  data-testid="inv-2fa-copy-codes"
                >
                  <Copy size={14} /> {t("invPortal.security.copyAll")}
                </button>
              </div>
            )}
          </div>
        )}
      </motion.section>

      {/* Sessions Section */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="rounded-3xl border border-sandBeige bg-white p-6 sm:p-7 shadow-soft relative overflow-hidden"
        data-testid="inv-security-sessions"
      >
        <div className="absolute top-0 left-0 w-16 h-1.5 rounded-bl-3xl rounded-tr-3xl bg-skyMist" aria-hidden />
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="wit-eyebrow text-raspberry mb-2">{t("invPortal.security.sessionsEyebrow")}</p>
            <h2 className="font-serif text-xl text-cocoa">{t("invPortal.security.sessionsTitle")}</h2>
          </div>
          {sessions.length > 1 && (
            <button type="button" onClick={revokeAll}
              className="text-xs text-coral hover:text-raspberry inline-flex items-center gap-1"
              data-testid="inv-revoke-all-btn"
            >
              <Trash2 size={12} /> {t("invPortal.security.revokeAll")}
            </button>
          )}
        </div>

        {sessionsLoading ? (
          <div className="flex items-center justify-center text-warmGrey gap-2 py-4">
            <Loader2 className="animate-spin" size={18} /> {t("invPortal.security.sessionsLoading")}
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-warmGrey text-sm">{t("invPortal.security.noSessions")}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center gap-3 p-3 rounded-2xl bg-porcelain border border-sandBeige" data-testid={`inv-session-${s.id}`}>
                <Monitor size={16} className="text-warmGrey shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-cocoa truncate">{s.user_agent || "Unknown device"}</p>
                  <p className="text-xs text-warmGrey">{t("invPortal.security.issuedAt")}: {formatDate(s.issued_at, lang)}</p>
                </div>
                <button type="button" onClick={() => revokeSession(s.id)}
                  className="text-xs text-coral hover:text-raspberry p-2 rounded-full hover:bg-roseMilk transition-colors"
                  data-testid={`inv-revoke-session-${s.id}`}
                  aria-label={t("invPortal.security.revokeOne")}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </motion.section>
    </div>
  );
}
